import { gql } from '@apollo/client';

export const USER_FIELDS = gql`
  fragment UserFields on User {
    id
    firstName
    lastName
    profilePicture
    numberOfStudios
  }
`;

export const STUDIO_FIELDS = gql`
  fragment StudioFields on Studio {
    id
    StudioName
    images
    studioLocation
    pricePerHour
    description
    userID
    studioTypes
    tagValues
    studioSize
    numberOfPeople
    city
    postcode
    country
    approxLatitude
    approxLongitude
    locationLabel
    locationPrecision
    minNumberOfHours
    amenities
    status
    createdAt
    updatedAt
  }
`;

export const STUDIO_WITH_RELATIONS_FIELDS = gql`
  fragment StudioWithRelationsFields on Studio {
    ...StudioFields
    User {
      ...UserFields
    }
    tags {
      nextToken
    }
    equipment {
      nextToken
    }
    availabilities {
      nextToken
    }
    bookings {
      nextToken
    }
    blockedTimes {
      nextToken
    }
    wishListStudios {
      nextToken
    }
    chatRooms {
      nextToken
    }
  }
  ${STUDIO_FIELDS}
  ${USER_FIELDS}
`;

export const GET_STUDIO = gql`
  query GetStudio($id: ID!) {
    getStudio(id: $id) {
      id
      StudioName
      images
      studioLocation
      city
      postcode
      pricePerHour
      description
      image
      userID
      studioTypes
      tagValues
      studioSize
      numberOfPeople
      city
      country
      approxLatitude
      approxLongitude
      locationLabel
      locationPrecision
      minNumberOfHours
      pricingMode
      tieredPricing {
        minPersons
        maxPersons
        pricePerHour
      }
      packages {
        id
        name
        durationHours
        prices {
          minPersons
          maxPersons
          price
        }
      }
      discountRules {
        minHours
        discountType
        discountValue
      }
      weekendSurcharge {
        saturdayEnabled
        saturdaySurchargeType
        saturdaySurchargeValue
        sundayEnabled
        sundaySurchargeType
        sundaySurchargeValue
      }
      extraFees {
        id
        name
        amount
      }
      paidEquipmentItems {
        items {
          id
          category
          name
          price
          quantity
          description
          imageKey
          sortOrder
        }
        nextToken
      }
      productionAreaSize
      amenities
      tags {
        items {
          tag {
            id
            name
          }
          id
          studioId
          tagId
          createdAt
          updatedAt
          __typename
        }
        nextToken
        __typename
      }
      equipment {
        items {
          id
          studioID
          category
          items {
            id
            name
            count
          }
        }
      }
      availabilities {
        items {
          id
          studioID
          dayOfWeek
          startTime
          endTime
        }
      }
      averageRating
      cancellationPolicy
      houseRules {
        smokingAllowed
        alcoholAllowed
        foodAllowed
        equipmentChangesAllowed
        pyrotechnicsAllowed
        glitterConfettiAllowed
        adultContentAllowed
        fogMachineAllowed
        customRules
      }
      User {
        id
        firstName
        lastName
        profilePicture
        averageRating
      }
      status
    }
  }
`;

// Public version of studio query for unauthenticated users
export const GET_STUDIO_PUBLIC = gql`
  query GetStudioPublic($id: ID!) {
    getStudio(id: $id) {
      id
      StudioName
      images
      studioLocation
      city
      postcode
      pricePerHour
      description
      image
      userID
      studioTypes
      studioSize
      numberOfPeople
      city
      country
      approxLatitude
      approxLongitude
      locationLabel
      locationPrecision
      minNumberOfHours
      pricingMode
      tieredPricing {
        minPersons
        maxPersons
        pricePerHour
      }
      packages {
        id
        name
        durationHours
        prices {
          minPersons
          maxPersons
          price
        }
      }
      discountRules {
        minHours
        discountType
        discountValue
      }
      weekendSurcharge {
        saturdayEnabled
        saturdaySurchargeType
        saturdaySurchargeValue
        sundayEnabled
        sundaySurchargeType
        sundaySurchargeValue
      }
      extraFees {
        id
        name
        amount
      }
      paidEquipmentItems {
        items {
          id
          category
          name
          price
          quantity
          description
          imageKey
          sortOrder
        }
        nextToken
      }
      productionAreaSize
      amenities
      tags {
        items {
          tag {
            id
            name
          }
          id
          studioId
          tagId
          createdAt
          updatedAt
          __typename
        }
        nextToken
        __typename
      }
      equipment {
        items {
          id
          studioID
          category
          items {
            id
            name
            count
          }
        }
      }
      availabilities {
        items {
          id
          studioID
          dayOfWeek
          startTime
          endTime
        }
      }
      averageRating
      cancellationPolicy
      houseRules {
        smokingAllowed
        alcoholAllowed
        foodAllowed
        equipmentChangesAllowed
        pyrotechnicsAllowed
        glitterConfettiAllowed
        adultContentAllowed
        fogMachineAllowed
        customRules
      }
      User {
        id
        firstName
        lastName
        profilePicture
        averageRating
      }
      status
    }
  }
`;

export const LIST_STUDIOS = gql`
  query ListStudios($filter: ModelStudioFilterInput, $limit: Int, $nextToken: String) {
    listStudios(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        ...StudioFields
      }
      nextToken
    }
  }
  ${STUDIO_FIELDS}
`;

export const STUDIOS_BY_USER_ID = gql`
  query StudiosByUserID(
    $userID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelStudioFilterInput
    $limit: Int
    $nextToken: String
  ) {
    studiosByUserID(
      userID: $userID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        ...StudioWithRelationsFields
      }
      nextToken
    }
  }
  ${STUDIO_WITH_RELATIONS_FIELDS}
`;

export const STUDIOS_IDS_BY_USER_ID = gql`
  query StudiosIdsByUserID(
    $userID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelStudioFilterInput
    $limit: Int
    $nextToken: String
  ) {
    studiosByUserID(
      userID: $userID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
      }
      nextToken
    }
  }
`;

export const STUDIOS_FOR_SERVICE_SELECTION = gql`
  query StudiosForServiceSelection(
    $userID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelStudioFilterInput
    $limit: Int
    $nextToken: String
  ) {
    studiosByUserID(
      userID: $userID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        StudioName
        studioLocation
        city
        country
        locationLabel
        studioTypes
        images
        status
      }
      nextToken
    }
  }
`;

export const HAS_STUDIOS = gql`
  query HasStudios($userID: ID!) {
    studiosByUserID(
      userID: $userID
      limit: 100
    ) {
      items {
        id
        status
      }
    }
  }
`;

export const STUDIOS_FOR_USER_PROFILE = gql`
  query StudiosForUserProfile(
    $userID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelStudioFilterInput
    $limit: Int
    $nextToken: String
  ) {
    studiosByUserID(
      userID: $userID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        status
        StudioName
        images
        studioLocation
        city
        country
        postcode
        studioTypes
        tagValues
        updatedAt
        userID
        User {
          id
          firstName
          lastName
          profilePicture
          averageRating
          reviewCount
          createdAt
          updatedAt
        }
      }
      nextToken
    }
  }
`;

export const GET_STUDIO_MINIMAL = gql`
  query GetStudioMinimal($id: ID!) {
    getStudio(id: $id) {
      id
      StudioName
      images
      studioLocation
      city
      country
      locationLabel
    }
  }
`;
